import { FormEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart3,
  BookOpenText,
  CheckCircle2,
  CircleAlert,
  FilePenLine,
  Inbox,
  Link2,
  LogOut,
  MessageCircleMore,
  MessageSquareText,
  Plus,
  Save,
  Settings2,
  Sparkles,
  Trash2,
  UsersRound,
} from "lucide-react";
import { api, dateLabel, id } from "../lib";
import type {
  AdminState,
  Block,
  ConnectionSettings,
  Faq,
  Page,
  Post,
  AdminUser,
  SiteSettings,
} from "../types";
import { AnalyticsDashboard } from "./AnalyticsDashboard";
import { ChatHistory } from "./ChatHistory";
import { Submissions } from "./Submissions";

type View =
  | "pages"
  | "faq"
  | "blog"
  | "automation"
  | "analytics"
  | "chat-history"
  | "submissions"
  | "settings"
  | "users";
type Props = {
  state: AdminState;
  csrf: string;
  refresh: () => Promise<void>;
  note: (value: string) => void;
};
const adminViews: View[] = [
  "analytics",
  "pages",
  "faq",
  "blog",
  "submissions",
  "chat-history",
  "users",
  "automation",
  "settings",
];
const storedAdminView = (): View => {
  const saved = window.sessionStorage.getItem("kim-admin-view-v2");
  return adminViews.includes(saved as View) ? (saved as View) : "analytics";
};
export function Admin() {
  const [state, setState] = useState<AdminState | null>(null);
  const [csrf, setCsrf] = useState("");
  const [view, setView] = useState<View>(storedAdminView);
  const [message, setMessage] = useState("");
  const [user, setUser] = useState("");
  const refresh = async () => {
    const me = await api<{ email: string; fullName: string; csrf: string }>("/api/auth/me");
    setCsrf(me.csrf);
    setUser(`${me.fullName} - ${me.email}`);
    setState(await api<AdminState>("/api/admin/state"));
  };
  useEffect(() => {
    refresh().catch(() => undefined);
  }, []);
  useEffect(() => {
    document.body.classList.add("admin-active");
    return () => document.body.classList.remove("admin-active");
  }, []);
  useEffect(() => {
    window.sessionStorage.setItem("kim-admin-view-v2", view);
  }, [view]);
  useEffect(() => {
    const openSettings = () => setView("settings");
    window.addEventListener("kim-open-oauth-settings", openSettings);
    return () =>
      window.removeEventListener("kim-open-oauth-settings", openSettings);
  }, []);
  useEffect(() => {
    if (!message) return;
    const timeout = window.setTimeout(() => setMessage(""), 4200);
    return () => window.clearTimeout(timeout);
  }, [message]);
  if (!state) return <AdminLogin onSuccess={refresh} />;
  const logout = async () => {
    await api("/api/auth/logout", { method: "POST" });
    setState(null);
  };
  const heading =
    view === "pages"
      ? "Website content"
      : view === "faq"
        ? "Frequently asked questions"
        : view === "blog"
          ? "Editorial engine"
          : view === "analytics"
            ? "Traffic & conversion activity"
            : view === "chat-history"
              ? "Chat history"
              : view === "submissions"
                ? "Submissions"
                : view === "settings"
                  ? "Connected accounts"
                  : view === "users"
                    ? "User manager"
                  : "Automation controls";
  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <Link to="/" className="wordmark">
          Kim Kelley<span>®</span>
        </Link>
        <p className="admin-kicker">EDITORIAL STUDIO</p>
        <nav>
          {(
            [
              ["analytics", BarChart3, "Analytics"],
              ["pages", FilePenLine, "Pages"],
              ["faq", MessageSquareText, "FAQ"],
              ["blog", BookOpenText, "Blog"],
              ["submissions", Inbox, "Submissions"],
              ["chat-history", MessageCircleMore, "Chat history"],
              ["users", UsersRound, "Users"],
              ["automation", Settings2, "Automation"],
              ["settings", Link2, "Settings"],
            ] as const
          ).map(([key, Icon, label]) => (
            <button
              className={view === key ? "active" : ""}
              onClick={() => setView(key)}
              key={key}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </nav>
        <div className="admin-account">
          <span>{user}</span>
          <button onClick={logout}>
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>
      <section className="admin-content">
        <header className="admin-header">
          <div>
            <p className="eyebrow">ONE KIM KELLEY</p>
            <h1>{heading}</h1>
          </div>
          <div className="publish-status">
            <CheckCircle2 size={16} /> Changes save directly to the selected
            environment.
          </div>
        </header>
        {message && <p className="admin-message">{message}</p>}
        {view === "pages" && (
          <PagesEditor
            state={state}
            csrf={csrf}
            refresh={refresh}
            note={setMessage}
          />
        )}{" "}
        {view === "faq" && (
          <FaqEditor
            state={state}
            csrf={csrf}
            refresh={refresh}
            note={setMessage}
          />
        )}{" "}
        {view === "blog" && (
          <BlogEditor
            state={state}
            csrf={csrf}
            refresh={refresh}
            note={setMessage}
          />
        )}{" "}
        {view === "analytics" && (
          <AnalyticsDashboard analytics={state.analytics} />
        )}{" "}
        {view === "submissions" && (
          <Submissions
            state={state}
            csrf={csrf}
            refresh={refresh}
            note={setMessage}
          />
        )}{" "}
        {view === "chat-history" && (
          <ChatHistory conversations={state.chatConversations} />
        )}{" "}
        {view === "users" && <UsersEditor csrf={csrf} note={setMessage} />}{" "}
        {view === "automation" && (
          <AutomationEditor
            state={state}
            csrf={csrf}
            refresh={refresh}
            note={setMessage}
          />
        )}{" "}
        {view === "settings" && (<>
          <SiteSettingsEditor
            settings={state.siteSettings}
            mediaAssets={state.mediaAssets}
            csrf={csrf}
            refresh={refresh}
            note={setMessage}
          />
          <ConnectionSettingsEditor
            settings={state.connectionSettings}
            integrations={state.integrations}
            csrf={csrf}
            refresh={refresh}
            note={setMessage}
          />
        </>)}
      </section>
    </div>
  );
}

function UsersEditor({ csrf, note }: { csrf: string; note: (value: string) => void }) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [draft, setDraft] = useState({ fullName: "", email: "", password: "", role: "editor" as AdminUser["role"] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const load = async () => {
    setLoading(true);
    try { setUsers((await api<{ users: AdminUser[] }>("/api/admin/users")).users); }
    catch (error) { note(error instanceof Error ? error.message : "Could not load users."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load().catch(() => undefined); }, []);
  const create = async (event: FormEvent) => {
    event.preventDefault(); setSaving(true);
    try {
      await api("/api/admin/users", { method: "POST", csrf, body: JSON.stringify(draft) });
      setDraft({ fullName: "", email: "", password: "", role: "editor" });
      note("User created. They can sign in now."); await load();
    } catch (error) { note(error instanceof Error ? error.message : "Could not create user."); }
    finally { setSaving(false); }
  };
  return <div className="users-layout">
    <form className="user-form-card" onSubmit={create}>
      <p className="eyebrow">NEW ACCESS</p><h2>Create user</h2>
      <p className="helper">Add another person who can sign in to the public-site admin.</p>
      <label>Full name<input value={draft.fullName} onChange={event => setDraft({ ...draft, fullName: event.target.value })} required /></label>
      <label>Login email<input type="email" value={draft.email} onChange={event => setDraft({ ...draft, email: event.target.value })} required /></label>
      <label>Temporary password<input type="password" value={draft.password} onChange={event => setDraft({ ...draft, password: event.target.value })} minLength={5} required /></label>
      <label>Access level<select value={draft.role} onChange={event => setDraft({ ...draft, role: event.target.value as AdminUser["role"] })}><option value="editor">Editor</option><option value="kim">Admin</option></select></label>
      <button className="button dark" disabled={saving}>{saving ? "Creating…" : "Create user"}</button>
    </form>
    <section className="user-list-card">
      <div className="editor-titlebar"><div><p className="eyebrow">AUTHORIZED PEOPLE</p><h2>Admin access</h2></div><span className="panel-note">{users.length} {users.length === 1 ? "user" : "users"}</span></div>
      {loading ? <p className="helper">Loading users…</p> : <div className="user-list">{users.map(user => <article className="user-row" key={user.id}><div><strong>{user.fullName}</strong><span>{user.email}</span><small>{user.active ? "Active" : "Inactive"}{user.lastLoginAt ? ` - Last sign-in ${dateLabel(user.lastLoginAt)}` : " - Not signed in yet"}</small></div><b>{user.role === "kim" ? "Admin" : "Editor"}</b></article>)}</div>}
    </section>
  </div>;
}

function AdminLogin({ onSuccess }: { onSuccess: () => Promise<void> }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await api("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      await onSuccess();
    } catch (error) {
      setError(error instanceof Error ? error.message : "Unable to sign in.");
    }
  };
  return (
    <div className="admin-login">
      <form onSubmit={submit}>
        <Link to="/" className="wordmark">
          Kim Kelley<span>®</span>
        </Link>
        <p className="eyebrow">EDITORIAL STUDIO</p>
        <h1>Sign in</h1>
        <p>Manage pages, FAQs, articles, and publishing controls.</p>
        <label>
          Email
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {location.hostname === "localhost" && (
          <p className="dev-note">Local demo: kim@local.test / change-me-now</p>
        )}
        {error && <p className="form-error">{error}</p>}
        <button className="button dark">Sign in</button>
      </form>
    </div>
  );
}

function SiteSettingsEditor({ settings, mediaAssets, csrf, refresh, note }: { settings: SiteSettings; mediaAssets: AdminState["mediaAssets"]; csrf: string; refresh: () => Promise<void>; note: (value: string) => void }) {
  const [draft, setDraft] = useState(settings); const [navigationJson, setNavigationJson] = useState(JSON.stringify(settings.navigationGroups, null, 2)); const [cohortJson, setCohortJson] = useState(JSON.stringify(settings.cohortWindows, null, 2)); const [uploading, setUploading] = useState(false);
  useEffect(() => { setDraft(settings); setNavigationJson(JSON.stringify(settings.navigationGroups, null, 2)); setCohortJson(JSON.stringify(settings.cohortWindows, null, 2)); }, [settings]);
  const save = async (event: FormEvent) => { event.preventDefault(); try { const next = { ...draft, navigationGroups: JSON.parse(navigationJson), cohortWindows: JSON.parse(cohortJson) } as SiteSettings; await api('/api/admin/site-settings', { method: 'PUT', csrf, body: JSON.stringify(next) }); note('Navigation, CTA, booking, cohort, and placeholder settings saved.'); await refresh(); } catch (error) { note(error instanceof Error ? error.message : 'Could not save site settings.'); } };
  const upload = async (file?: File) => { if (!file) return; setUploading(true); try { const response = await fetch('/api/admin/media', { method: 'POST', credentials: 'include', headers: { 'content-type': file.type || 'application/octet-stream', 'x-file-name': file.name, 'x-csrf-token': csrf }, body: file }); if (!response.ok) { const body = await response.json().catch(() => ({})); throw new Error(body.error || 'Upload failed.'); } note('Media uploaded as a draft. Verify and publish it before adding it to a public block.'); await refresh(); } catch (error) { note(error instanceof Error ? error.message : 'Could not upload media.'); } finally { setUploading(false); } };
  const updateAsset = async (asset: AdminState['mediaAssets'][number], workflowStatus: 'draft'|'verified'|'published') => { try { await api(`/api/admin/media/${asset.id}`, { method: 'PATCH', csrf, body: JSON.stringify({ workflowStatus, sourceNotes: asset.sourceNotes, ...(workflowStatus === 'published' ? { approvedAt: new Date().toISOString() } : {}) }) }); note(`Media marked ${workflowStatus}.`); await refresh(); } catch (error) { note(error instanceof Error ? error.message : 'Could not update media.'); } };
  return <section className="site-settings-card"><div className="editor-titlebar"><div><p className="eyebrow">SITE EXPERIENCE</p><h2>Navigation, CTAs &amp; scheduling</h2></div></div><form onSubmit={save}><div className="form-row"><AdminField label="General CTA" value={draft.ctas.general.label} onChange={label => setDraft({...draft,ctas:{...draft.ctas,general:{...draft.ctas.general,label}}})}/><AdminField label="General CTA URL" value={draft.ctas.general.href} onChange={href => setDraft({...draft,ctas:{...draft.ctas,general:{...draft.ctas.general,href}}})}/></div><div className="form-row"><AdminField label="Speaking CTA" value={draft.ctas.speaking.label} onChange={label => setDraft({...draft,ctas:{...draft.ctas,speaking:{...draft.ctas.speaking,label},media:{...draft.ctas.media,label}}})}/><AdminField label="Workshop CTA" value={draft.ctas.workshops.label} onChange={label => setDraft({...draft,ctas:{...draft.ctas,workshops:{...draft.ctas.workshops,label}}})}/></div><div className="form-row"><AdminField label="Booking hours start (CT)" value={draft.booking.businessHours[0]?.start || '09:00'} onChange={start => setDraft({...draft,booking:{...draft.booking,businessHours:[{...(draft.booking.businessHours[0] || {days:[1,2,3,4,5],end:'16:00'}),start}]}})}/><AdminField label="Booking hours end (CT)" value={draft.booking.businessHours[0]?.end || '16:00'} onChange={end => setDraft({...draft,booking:{...draft.booking,businessHours:[{...(draft.booking.businessHours[0] || {days:[1,2,3,4,5],start:'09:00'}),end}]}})}/></div><label>Grouped navigation JSON<textarea className="code-field" value={navigationJson} onChange={event => setNavigationJson(event.target.value)}/></label><label>Cohort windows JSON<textarea className="code-field" value={cohortJson} onChange={event => setCohortJson(event.target.value)}/></label><div className="form-row"><AdminField label="Speaking reel placeholder" value={draft.placeholders.speakingReel} onChange={speakingReel => setDraft({...draft,placeholders:{...draft.placeholders,speakingReel}})}/><AdminField label="Speaker kit placeholder" value={draft.placeholders.speakerKit} onChange={speakerKit => setDraft({...draft,placeholders:{...draft.placeholders,speakerKit}})}/></div><button className="button dark"><Save size={16}/>Save site experience</button></form><div className="media-library"><div><p className="eyebrow">DURABLE MEDIA LIBRARY</p><h3>Approved assets survive deployments</h3><p className="helper">Uploads begin as drafts. Publish only after source and approval review.</p></div><label className="media-upload">{uploading ? 'Uploading…' : 'Upload image, PDF, or video'}<input type="file" accept="image/*,application/pdf,video/mp4,video/webm" disabled={uploading} onChange={event => upload(event.target.files?.[0])}/></label><div className="media-asset-list">{mediaAssets.map(asset => <article key={asset.id}><div><strong>{asset.fileName}</strong><span>{asset.workflowStatus} · {Math.ceil(asset.size/1024)} KB</span><code>{asset.publicPath}</code></div><div><button type="button" onClick={() => updateAsset(asset,'verified')}>Verify</button><button type="button" onClick={() => updateAsset(asset,'published')}>Publish</button></div></article>)}</div></div></section>;
}

function ConnectionSettingsEditor({
  settings,
  integrations,
  csrf,
  refresh,
  note,
}: {
  settings: ConnectionSettings;
  integrations: AdminState["integrations"];
  csrf: string;
  refresh: () => Promise<void>;
  note: (value: string) => void;
}) {
  const [draft, setDraft] = useState(settings);
  const [clearingCache, setClearingCache] = useState(false);
  useEffect(() => setDraft(settings), [settings]);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await api("/api/admin/connection-settings", {
        method: "PUT",
        body: JSON.stringify(draft),
        csrf,
      });
      note("Connected-account settings saved.");
      await refresh();
    } catch (error) {
      note(
        error instanceof Error
          ? error.message
          : "Could not save connected-account settings.",
      );
    }
  };
  const disconnectGoogle = async () => {
    if (
      !window.confirm(
        "Disconnect Google Calendar? Future website bookings will remain saved, but will no longer create calendar events until another account connects.",
      )
    )
      return;
    try {
      await api("/api/admin/oauth/google/disconnect", { method: "POST", csrf });
      note(
        "Google Calendar disconnected. Connect Kim’s Google account to switch calendars.",
      );
      await refresh();
    } catch (error) {
      note(
        error instanceof Error
          ? error.message
          : "Could not disconnect Google Calendar.",
      );
    }
  };
  const clearCache = async () => {
    setClearingCache(true);
    try {
      await api<{ clearedAt: string }>("/api/admin/cache/clear", {
        method: "POST",
        csrf,
      });
      note("Public-site cache cleared. New content is available now.");
    } catch (error) {
      note(
        error instanceof Error
          ? error.message
          : "Could not clear the public-site cache.",
      );
    } finally {
      setClearingCache(false);
    }
  };
  return (
    <form className="connection-settings" onSubmit={save}>
      <section>
        <p className="eyebrow">GOOGLE / GMAIL</p>
        <h2>Choose the account to connect.</h2>
        <p>
          Enter the Google or Google Workspace address that should receive
          website lead notifications and be authorized for calendar scheduling.
        </p>
        <label>
          Google account email
          <input
            type="email"
            value={draft.gmailAccountEmail}
            onChange={(event) =>
              setDraft({ ...draft, gmailAccountEmail: event.target.value })
            }
            placeholder="name@company.com"
          />
        </label>
        <div
          className={`connection-status ${integrations.calendar.configured ? "connected" : ""}`}
        >
          <strong>
            {integrations.calendar.configured
              ? "Google Calendar connected"
              : "Calendar authorization needed"}
          </strong>
          <span>{integrations.calendar.message}</span>
          {integrations.calendar.configured ? (
            <button
              className="oauth-connect-button disconnect"
              type="button"
              onClick={disconnectGoogle}
            >
              Disconnect calendar
            </button>
          ) : (
            <button
              className="oauth-connect-button"
              type="button"
              onClick={() => window.location.assign("/api/oauth/google/start")}
            >
              <Link2 size={13} /> Connect Google Calendar
            </button>
          )}
        </div>
      </section>
      <section>
        <p className="eyebrow">LINKEDIN</p>
        <h2>Connect Kim Kelley’s publishing account.</h2>
        <p>
          Connect Kim Kelley’s own LinkedIn profile through LinkedIn OAuth. The
          site will publish the article summary, article link, and relevant Kim
          Kelley workshop or service links.
        </p>
        <label>
          LinkedIn account email
          <input
            type="email"
            value={draft.linkedinAccountEmail}
            onChange={(event) =>
              setDraft({ ...draft, linkedinAccountEmail: event.target.value })
            }
            placeholder="name@company.com"
          />
        </label>
        <div
          className={`connection-status ${integrations.linkedin.configured ? "connected" : ""}`}
        >
          <strong>
            {integrations.linkedin.configured
              ? "OAuth app configured"
              : "OAuth setup pending"}
          </strong>
          <span>{integrations.linkedin.message}</span>
          {integrations.linkedin.configured ? (
            <button
              className="oauth-connect-button disconnect"
              type="button"
              onClick={async () => {
                if (!window.confirm("Disconnect Kim Kelley’s LinkedIn profile? Automatic publishing will stop until it is connected again.")) return;
                try {
                  await api("/api/admin/oauth/linkedin/disconnect", { method: "POST", csrf });
                  note("LinkedIn disconnected. No future posts will be sent.");
                  await refresh();
                } catch (error) {
                  note(error instanceof Error ? error.message : "Could not disconnect LinkedIn.");
                }
              }}
            >
              Disconnect LinkedIn
            </button>
          ) : (
            <button
              className="oauth-connect-button"
              type="button"
              onClick={() => window.location.assign("/api/oauth/linkedin/start")}
            >
              <Link2 size={13} /> Connect Kim Kelley LinkedIn
            </button>
          )}
        </div>
      </section>
      <section className="cache-management">
        <p className="eyebrow">SITE CACHE</p>
        <h2>Refresh the public site.</h2>
        <p>
          Public pages are cached for five minutes for faster delivery. Content
          edits clear this cache automatically; use this control after a
          deployment or whenever you want to refresh it immediately.
        </p>
        <button
          className="button dark"
          type="button"
          onClick={clearCache}
          disabled={clearingCache}
        >
          {clearingCache ? "Clearing cache…" : "Clear public-site cache"}
        </button>
      </section>
      <div className="connection-settings-footer">
        <p>
          Save the intended owner first, then connect Google Calendar. The
          authorization grants event creation and narrow FreeBusy access for
          live availability; Brevo continues to deliver lead-email notifications.
        </p>
        <button className="button dark">
          <Save size={16} /> Save account settings
        </button>
      </div>
    </form>
  );
}

function PagesEditor({ state, csrf, refresh, note }: Props) {
  const [selectedId, setSelectedId] = useState(
    state.pages.find((item) => !item.deletedAt)?.id,
  );
  const [showTrash, setShowTrash] = useState(false);
  const activePages = state.pages.filter((item) => !item.deletedAt);
  const trashedPages = state.pages.filter((item) => item.deletedAt);
  const page =
    activePages.find((item) => item.id === selectedId) ?? activePages[0];
  if (!page && !showTrash) return null;
  const select = (id: string) => {
    setShowTrash(false);
    setSelectedId(id);
    note("");
  };
  const save = async (next: Page, creating = false) => {
    try {
      await api(`/api/admin/pages${creating ? "" : `/${next.id}`}`, {
        method: creating ? "POST" : "PUT",
        body: JSON.stringify(next),
        csrf,
      });
      note(creating ? "New page created." : "Page saved.");
      await refresh();
      setSelectedId(next.id);
    } catch (error) {
      note(error instanceof Error ? error.message : "Could not save.");
    }
  };
  const remove = async (pageToDelete: Page) => {
    if (
      pageToDelete.id === "home" ||
      !window.confirm(
        `Move “${pageToDelete.title}” to Trash? You can restore it later.`,
      )
    )
      return;
    try {
      await api(`/api/admin/pages/${pageToDelete.id}`, {
        method: "DELETE",
        csrf,
      });
      const nextPage = activePages.find((item) => item.id !== pageToDelete.id);
      setSelectedId(nextPage?.id ?? "home");
      note("Page moved to Trash.");
      await refresh();
    } catch (error) {
      note(
        error instanceof Error
          ? error.message
          : "Could not move page to Trash.",
      );
    }
  };
  const restore = async (pageToRestore: Page) => {
    try {
      await api(`/api/admin/pages/${pageToRestore.id}/restore`, {
        method: "POST",
        csrf,
      });
      setShowTrash(false);
      setSelectedId(pageToRestore.id);
      note("Page restored.");
      await refresh();
    } catch (error) {
      note(error instanceof Error ? error.message : "Could not restore page.");
    }
  };
  const purge = async (pageToPurge: Page) => {
    if (
      !window.confirm(
        `Permanently delete “${pageToPurge.title}”? This cannot be undone.`,
      )
    )
      return;
    try {
      await api(`/api/admin/pages/${pageToPurge.id}/permanent`, {
        method: "DELETE",
        csrf,
      });
      note("Page permanently deleted.");
      await refresh();
    } catch (error) {
      note(
        error instanceof Error
          ? error.message
          : "Could not permanently delete page.",
      );
    }
  };
  const create = () => {
    const next: Page = {
      id: id(),
      slug: "/new-page",
      title: "New page",
      seoTitle: "New page | Kim Kelley",
      seoDescription: "Describe this new page for search.",
      kind: "standard",
      status: "draft",
      navigationLabel: "New page",
      updatedAt: new Date().toISOString(),
      blocks: [
        {
          id: id(),
          type: "hero",
          label: "Page introduction",
          data: {
            eyebrow: "NEW PAGE",
            heading: "A clear new page",
            body: "Write the page introduction here.",
            primaryCta: "Work with Kim",
            primaryHref: "/apply",
            poster: "/images/hero-photo.jpg",
          },
        },
      ],
    };
    save(next, true);
  };
  return (
    <div className="editor-grid">
      <aside className="content-list">
        <button className="admin-add" onClick={create}>
          <Plus size={16} /> Create page
        </button>
        <button
          className={`trash-toggle ${showTrash ? "selected" : ""}`}
          onClick={() => setShowTrash(!showTrash)}
        >
          Trash {trashedPages.length ? `(${trashedPages.length})` : ""}
        </button>
        {activePages.map((item) => (
          <button
            className={item.id === page?.id && !showTrash ? "selected" : ""}
            onClick={() => select(item.id)}
            key={item.id}
          >
            <span>{item.title}</span>
            <small>{item.status}</small>
          </button>
        ))}
      </aside>
      {showTrash ? (
        <section className="page-trash">
          <div>
            <p className="eyebrow">PAGE TRASH</p>
            <h2>Recover deleted pages</h2>
            <p className="helper">
              Trashed pages are unavailable publicly and can be restored
              whenever needed.
            </p>
          </div>
          {trashedPages.length ? (
            trashedPages.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>{item.title}</strong>
                  <span>
                    {item.slug} · deleted{" "}
                    {new Date(item.deletedAt!).toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <button className="button dark" onClick={() => restore(item)}>
                    Restore
                  </button>
                  <button className="button danger" onClick={() => purge(item)}>
                    Delete forever
                  </button>
                </div>
              </article>
            ))
          ) : (
            <p className="helper">No pages are in Trash.</p>
          )}
        </section>
      ) : (
        page && <PageForm page={page} onSave={save} onDelete={remove} />
      )}
    </div>
  );
}
function PageForm({
  page,
  onSave,
  onDelete,
}: {
  page: Page;
  onSave: (page: Page) => Promise<void>;
  onDelete: (page: Page) => Promise<void>;
}) {
  const [draft, setDraft] = useState(page);
  useEffect(() => setDraft(page), [page]);
  const change = (key: keyof Page, value: unknown) =>
    setDraft({ ...draft, [key]: value });
  const save = (event: FormEvent) => {
    event.preventDefault();
    onSave(draft);
  };
  return (
    <form className="page-form" onSubmit={save}>
      <div className="editor-titlebar">
        <div>
          <p className="eyebrow">PAGE SETTINGS</p>
          <h2>{draft.title}</h2>
        </div>
        <div className="page-actions">
          <button className="button dark">
            <Save size={16} /> Save page
          </button>
          {draft.id !== "home" && (
            <button
              className="button danger"
              type="button"
              onClick={() => onDelete(draft)}
            >
              <Trash2 size={15} /> Delete page
            </button>
          )}
        </div>
      </div>
      <div className="form-row">
        <AdminField
          label="Internal title"
          value={draft.title}
          onChange={(v) => change("title", v)}
        />
        <AdminField
          label="URL path"
          value={draft.slug}
          onChange={(v) => change("slug", v)}
        />
      </div>
      <div className="form-row">
        <AdminField
          label="Search title"
          value={draft.seoTitle}
          onChange={(v) => change("seoTitle", v)}
        />
        <AdminField
          label="Navigation label"
          value={draft.navigationLabel}
          onChange={(v) => change("navigationLabel", v)}
        />
      </div>
      <AdminField
        label="Search description"
        value={draft.seoDescription}
        onChange={(v) => change("seoDescription", v)}
        textarea
      />
      <label>
        Publishing status
        <select
          value={draft.status}
          onChange={(e) => change("status", e.target.value)}
        >
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      </label>
      <div className="block-stack">
        <div>
          <p className="eyebrow">GUIDED BLOCK EDITOR</p>
          <h3>Page sections</h3>
          <p className="helper">
            Edit each section in context. Reordering and additional block types
            are ready for the next editorial pass.
          </p>
        </div>
        {draft.blocks.map((block, index) => (
          <BlockEditor
            block={block}
            key={block.id}
            onChange={(next) =>
              change(
                "blocks",
                draft.blocks.map((item, position) =>
                  position === index ? next : item,
                ),
              )
            }
          />
        ))}
      </div>
    </form>
  );
}
function BlockEditor({
  block,
  onChange,
}: {
  block: Block;
  onChange: (block: Block) => void;
}) {
  const data = block.data;
  const set = (key: string, value: string) =>
    onChange({ ...block, data: { ...data, [key]: value } });
  const common = (
    <>
      <AdminField
        label="Section label"
        value={block.label}
        onChange={(value) => onChange({ ...block, label: value })}
      />
      {["hero", "cta", "richText", "proof", "contact"].includes(block.type) && (
        <AdminField
          label="Eyebrow / kicker"
          value={String(data.eyebrow ?? data.kicker ?? "")}
          onChange={(value) =>
            set(data.eyebrow !== undefined ? "eyebrow" : "kicker", value)
          }
        />
      )}
      <AdminField
        label="Heading"
        value={String(data.heading ?? "")}
        onChange={(value) => set("heading", value)}
      />
      <AdminField
        label="Supporting text"
        value={String(data.body ?? data.quote ?? "")}
        onChange={(value) =>
          set(data.quote !== undefined ? "quote" : "body", value)
        }
        textarea
      />
    </>
  );
  return (
    <article className="block-editor">
      <div className="block-label">
        <span>{block.type}</span>
        <strong>{block.label}</strong>
      </div>
      {common}
      <div className="form-row">
        <label>Content state<select value={block.workflowStatus ?? "published"} onChange={event => onChange({ ...block, workflowStatus: event.target.value as Block["workflowStatus"] })}><option value="draft">Draft</option><option value="verified">Verified</option><option value="published">Published</option></select></label>
        <AdminField label="Approval date" value={block.approvedAt ?? ""} onChange={approvedAt => onChange({ ...block, approvedAt: approvedAt || undefined })}/>
      </div>
      <AdminField label="Source notes (one per line)" value={(block.sourceNotes ?? []).join("\n")} onChange={value => onChange({ ...block, sourceNotes: value.split(/\r?\n/).map(item => item.trim()).filter(Boolean) })} textarea/>
      {block.type === "hero" && (
        <div className="form-row">
          <AdminField
            label="Primary button"
            value={String(data.primaryCta ?? "")}
            onChange={(value) => set("primaryCta", value)}
          />
          <AdminField
            label="Button URL"
            value={String(data.primaryHref ?? "")}
            onChange={(value) => set("primaryHref", value)}
          />
        </div>
      )}
      {block.type === "cta" && (
        <div className="form-row">
          <AdminField
            label="Button label"
            value={String(data.button ?? "")}
            onChange={(value) => set("button", value)}
          />
          <AdminField
            label="Button URL"
            value={String(data.href ?? "")}
            onChange={(value) => set("href", value)}
          />
        </div>
      )}
      {["tiers", "faq"].includes(block.type) && (
        <p className="helper">
          This is a connected content block. Manage individual packages or
          questions from its dedicated editor.
        </p>
      )}
      {["mediaProof","caseStudies","downloads","cohortAvailability","learningOutcomes","procurementDetails","accordions"].includes(block.type) && <label>Advanced block data (JSON)<textarea className="code-field" defaultValue={JSON.stringify(data, null, 2)} onBlur={event => { try { onChange({ ...block, data: JSON.parse(event.currentTarget.value) }); } catch { event.currentTarget.value = JSON.stringify(data, null, 2); } }}/></label>}
    </article>
  );
}
function AdminField({
  label,
  value,
  onChange,
  textarea = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  textarea?: boolean;
}) {
  return (
    <label>
      {label}
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}

function escapeEditorHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function inlineMarkdownToEditorHtml(value: string) {
  return escapeEditorHtml(value)
    .replace(
      /\[([^\]]+)]\(((?:https?:\/\/|\/)[^\s)]+)\)/g,
      '<a href="$2">$1</a>',
    )
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/__([^_]+)__/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/_([^_]+)_/g, "<em>$1</em>");
}

function markdownToEditorHtml(value: string) {
  const lines = value.replace(/\r\n?/g, "\n").split("\n");
  const blocks: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let listItems: string[] = [];
  const flushList = () => {
    if (!listType || !listItems.length) return;
    blocks.push(`<${listType}>${listItems.map((item) => `<li>${item}</li>`).join("")}</${listType}>`);
    listType = null;
    listItems = [];
  };
  for (const line of lines) {
    const trimmed = line.trim();
    const unordered = trimmed.match(/^[-*]\s+(.+)$/);
    const ordered = trimmed.match(/^\d+[.)]\s+(.+)$/);
    if (unordered || ordered) {
      const nextType = unordered ? "ul" : "ol";
      if (listType && listType !== nextType) flushList();
      listType = nextType;
      listItems.push(inlineMarkdownToEditorHtml((unordered ?? ordered)![1]));
      continue;
    }
    flushList();
    if (!trimmed) continue;
    if (trimmed.startsWith("### ")) {
      blocks.push(`<h3>${inlineMarkdownToEditorHtml(trimmed.slice(4))}</h3>`);
    } else if (trimmed.startsWith("## ")) {
      blocks.push(`<h2>${inlineMarkdownToEditorHtml(trimmed.slice(3))}</h2>`);
    } else if (trimmed.startsWith("> ")) {
      blocks.push(`<blockquote>${inlineMarkdownToEditorHtml(trimmed.slice(2))}</blockquote>`);
    } else {
      blocks.push(`<p>${inlineMarkdownToEditorHtml(trimmed)}</p>`);
    }
  }
  flushList();
  return blocks.join("") || "<p><br></p>";
}

function inlineEditorNodeToMarkdown(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
  if (!(node instanceof HTMLElement)) return "";
  const content = Array.from(node.childNodes)
    .map(inlineEditorNodeToMarkdown)
    .join("");
  const tag = node.tagName.toLowerCase();
  if (tag === "strong" || tag === "b") return `**${content}**`;
  if (tag === "em" || tag === "i") return `*${content}*`;
  if (tag === "a") return `[${content}](${node.getAttribute("href") ?? ""})`;
  if (tag === "br") return "\n";
  return content;
}

function editorNodeToMarkdown(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent?.trim() ?? "";
  if (!(node instanceof HTMLElement)) return "";
  const tag = node.tagName.toLowerCase();
  if (tag === "ul" || tag === "ol") {
    return Array.from(node.children)
      .map((item, index) => `${tag === "ol" ? `${index + 1}. ` : "- "}${inlineEditorNodeToMarkdown(item)}`)
      .join("\n");
  }
  const content = inlineEditorNodeToMarkdown(node).trim();
  if (!content) return "";
  if (tag === "h2") return `## ${content}`;
  if (tag === "h3") return `### ${content}`;
  if (tag === "blockquote") return `> ${content}`;
  return content;
}

function editorHtmlToMarkdown(value: string) {
  const root = document.createElement("div");
  root.innerHTML = value;
  return Array.from(root.childNodes)
    .map(editorNodeToMarkdown)
    .filter(Boolean)
    .join("\n\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function WysiwygEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const editorRef = useRef<HTMLDivElement>(null);
  const focusedRef = useRef(false);
  useEffect(() => {
    if (editorRef.current && !focusedRef.current) {
      editorRef.current.innerHTML = markdownToEditorHtml(value);
    }
  }, [value]);
  const sync = () => {
    if (editorRef.current) onChange(editorHtmlToMarkdown(editorRef.current.innerHTML));
  };
  const command = (name: string, commandValue?: string) => {
    editorRef.current?.focus();
    document.execCommand(name, false, commandValue);
    sync();
  };
  const addLink = () => {
    const href = window.prompt("Paste the link URL");
    if (href?.trim()) command("createLink", href.trim());
  };
  return (
    <div className="wysiwyg-editor">
      <div className="wysiwyg-toolbar" role="toolbar" aria-label="Article formatting">
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => command("formatBlock", "<p>")} aria-label="Paragraph">P</button>
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => command("formatBlock", "<h2>")} aria-label="Heading 2">H2</button>
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => command("formatBlock", "<h3>")} aria-label="Heading 3">H3</button>
        <span className="wysiwyg-divider" />
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => command("bold")} aria-label="Bold"><strong>B</strong></button>
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => command("italic")} aria-label="Italic"><em>I</em></button>
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => command("insertUnorderedList")} aria-label="Bulleted list">• List</button>
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => command("insertOrderedList")} aria-label="Numbered list">1. List</button>
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => command("formatBlock", "<blockquote>")} aria-label="Quote">“ Quote</button>
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={addLink} aria-label="Add link">Link</button>
        <span className="wysiwyg-divider" />
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => command("undo")} aria-label="Undo">Undo</button>
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => command("redo")} aria-label="Redo">Redo</button>
      </div>
      <div
        ref={editorRef}
        className="wysiwyg-content"
        contentEditable
        suppressContentEditableWarning
        onFocus={() => { focusedRef.current = true; }}
        onBlur={() => { focusedRef.current = false; sync(); }}
        onInput={sync}
        role="textbox"
        aria-label="Article body"
      />
      <p className="wysiwyg-help">Use the toolbar to format the article. Links, headings, emphasis, lists, and quotes are saved with the post.</p>
    </div>
  );
}

function FaqEditor({ state, csrf, refresh, note }: Props) {
  const [selectedId, setSelectedId] = useState(state.faqs[0]?.id);
  const selected =
    state.faqs.find((item) => item.id === selectedId) ?? state.faqs[0];
  const [draft, setDraft] = useState(selected);
  useEffect(() => setDraft(selected), [selected]);
  const save = async (next: Faq) => {
    try {
      await api(`/api/admin/faqs/${next.id}`, {
        method: "PUT",
        body: JSON.stringify(next),
        csrf,
      });
      note("FAQ saved.");
      await refresh();
      setSelectedId(next.id);
    } catch (error) {
      note(error instanceof Error ? error.message : "Could not save FAQ.");
    }
  };
  const add = () =>
    save({
      id: id(),
      category: "General",
      question: "New question",
      answer: "Write a clear, useful answer.",
      position: state.faqs.length + 1,
      published: false,
    });
  if (!selected || !draft) return null;
  return (
    <div className="editor-grid">
      <aside className="content-list">
        <button className="admin-add" onClick={add}>
          <Plus size={16} /> Add FAQ
        </button>
        {state.faqs.map((item) => (
          <button
            className={item.id === selected.id ? "selected" : ""}
            onClick={() => setSelectedId(item.id)}
            key={item.id}
          >
            <span>{item.question}</span>
            <small>{item.category}</small>
          </button>
        ))}
      </aside>
      <form
        className="page-form"
        onSubmit={(event) => {
          event.preventDefault();
          save(draft);
        }}
      >
        <div className="editor-titlebar">
          <div>
            <p className="eyebrow">FAQ EDITOR</p>
            <h2>Answer with clarity</h2>
          </div>
          <button className="button dark">
            <Save size={16} /> Save FAQ
          </button>
        </div>
        <label>
          Category
          <select
            value={draft.category}
            onChange={(e) =>
              setDraft({
                ...draft,
                category: e.target.value as Faq["category"],
              })
            }
          >
            <option>Assessment</option>
            <option>Advisory</option>
            <option>Speaking</option>
            <option>General</option>
          </select>
        </label>
        <AdminField
          label="Question"
          value={draft.question}
          onChange={(value) => setDraft({ ...draft, question: value })}
        />
        <AdminField
          label="Answer"
          value={draft.answer}
          onChange={(value) => setDraft({ ...draft, answer: value })}
          textarea
        />
        <label className="checkbox">
          <input
            type="checkbox"
            checked={draft.published}
            onChange={(e) =>
              setDraft({ ...draft, published: e.target.checked })
            }
          />{" "}
          Publish this answer
        </label>
        <p className="helper">
          Tip: FAQs are rendered as accessible expanding panels and structured
          for FAQ-page search markup during production deployment.
        </p>
      </form>
    </div>
  );
}

function BlogEditor({ state, csrf, refresh, note }: Props) {
  const [selectedId, setSelectedId] = useState(
    state.posts.find((item) => !item.deletedAt)?.id,
  );
  const [topic, setTopic] = useState(
    "How leaders measure workforce readiness before committing more capital",
  );
  const [generating, setGenerating] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const activePosts = state.posts.filter((item) => !item.deletedAt);
  const trashedPosts = state.posts.filter((item) => item.deletedAt);
  const post =
    activePosts.find((item) => item.id === selectedId) ?? activePosts[0];
  const save = async (next: Post) => {
    try {
      await api(`/api/admin/posts/${next.id}`, {
        method: "PUT",
        body: JSON.stringify(next),
        csrf,
      });
      note("Article saved.");
      await refresh();
    } catch (error) {
      note(error instanceof Error ? error.message : "Could not save article.");
    }
  };
  const remove = async (postToDelete: Post) => {
    if (
      !window.confirm(
        `Move “${postToDelete.title}” to Trash? You can restore it later.`,
      )
    )
      return;
    try {
      await api(`/api/admin/posts/${postToDelete.id}`, {
        method: "DELETE",
        csrf,
      });
      setSelectedId(
        activePosts.find((item) => item.id !== postToDelete.id)?.id,
      );
      note("Article moved to Trash.");
      await refresh();
    } catch (error) {
      note(
        error instanceof Error
          ? error.message
          : "Could not move article to Trash.",
      );
    }
  };
  const restore = async (postToRestore: Post) => {
    try {
      await api(`/api/admin/posts/${postToRestore.id}/restore`, {
        method: "POST",
        csrf,
      });
      setShowTrash(false);
      setSelectedId(postToRestore.id);
      note("Article restored.");
      await refresh();
    } catch (error) {
      note(
        error instanceof Error ? error.message : "Could not restore article.",
      );
    }
  };
  const purge = async (postToPurge: Post) => {
    if (
      !window.confirm(
        `Permanently delete “${postToPurge.title}”? This cannot be undone.`,
      )
    )
      return;
    try {
      await api(`/api/admin/posts/${postToPurge.id}/permanent`, {
        method: "DELETE",
        csrf,
      });
      note("Article permanently deleted.");
      await refresh();
    } catch (error) {
      note(
        error instanceof Error
          ? error.message
          : "Could not permanently delete article.",
      );
    }
  };
  const generate = async () => {
    setGenerating(true);
    try {
      const created = await api<Post>("/api/admin/posts/generate", {
        method: "POST",
        body: JSON.stringify({ topic, namedSources: [] }),
        csrf,
      });
      note("A new AI draft is ready for editorial review.");
      await refresh();
      setSelectedId(created.id);
    } catch (error) {
      note(
        error instanceof Error ? error.message : "Generation is unavailable.",
      );
    } finally {
      setGenerating(false);
    }
  };
  return (
    <>
      <section className="automation-card generation-card">
        <div>
          <p className="eyebrow">AI DRAFT STUDIO</p>
          <h2>Start with a focused leadership question.</h2>
          <p>{state.integrations.generator.message}</p>
        </div>
        <div>
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            aria-label="Article topic"
          />
          <button
            className="button dark"
            onClick={generate}
            disabled={generating || !state.integrations.generator.configured}
          >
            <Sparkles size={16} />
            {generating ? "Generating…" : "Generate draft"}
          </button>
        </div>
      </section>
      <div className="editor-grid">
        <aside className="content-list">
          <button
            className={`trash-toggle ${showTrash ? "selected" : ""}`}
            onClick={() => setShowTrash(!showTrash)}
          >
            Trash {trashedPosts.length ? `(${trashedPosts.length})` : ""}
          </button>
          {activePosts.map((item) => (
            <button
              className={item.id === post?.id && !showTrash ? "selected" : ""}
              onClick={() => {
                setShowTrash(false);
                setSelectedId(item.id);
              }}
              key={item.id}
            >
              <span>{item.title}</span>
              <small>{item.status}</small>
            </button>
          ))}
        </aside>
        {showTrash ? (
          <section className="page-trash">
            <div>
              <p className="eyebrow">ARTICLE TRASH</p>
              <h2>Recover deleted articles</h2>
              <p className="helper">
                Trashed articles are unavailable publicly and can be restored
                whenever needed.
              </p>
            </div>
            {trashedPosts.length ? (
              trashedPosts.map((item) => (
                <article key={item.id}>
                  <div>
                    <strong>{item.title}</strong>
                    <span>
                      /blog/{item.slug} · deleted{" "}
                      {new Date(item.deletedAt!).toLocaleDateString()}
                    </span>
                  </div>
                  <div>
                    <button
                      className="button dark"
                      onClick={() => restore(item)}
                    >
                      Restore
                    </button>
                    <button
                      className="button danger"
                      onClick={() => purge(item)}
                    >
                      Delete forever
                    </button>
                  </div>
                </article>
              ))
            ) : (
              <p className="helper">No articles are in Trash.</p>
            )}
          </section>
        ) : post ? (
          <PostForm post={post} save={save} onDelete={remove} />
        ) : (
          <section className="page-trash">
            <p className="helper">No active articles yet.</p>
          </section>
        )}
      </div>
    </>
  );
}
function PostForm({
  post,
  save,
  onDelete,
}: {
  post: Post;
  save: (post: Post) => Promise<void>;
  onDelete: (post: Post) => Promise<void>;
}) {
  const [draft, setDraft] = useState(post);
  useEffect(() => setDraft(post), [post]);
  const set = <K extends keyof Post>(key: K, value: Post[K]) =>
    setDraft({ ...draft, [key]: value });
  return (
    <form
      className="page-form post-editor-form"
      onSubmit={(event) => {
        event.preventDefault();
        save(draft);
      }}
    >
      <div className="editor-titlebar">
        <div>
          <p className="eyebrow">ARTICLE EDITOR</p>
          <h2>
            {draft.status === "draft" ? "Draft article" : "Published article"}
          </h2>
          <p className="post-editor-subtitle">Shape the story, check the details, and publish with confidence.</p>
        </div>
        <div className="page-actions">
          <span className={`status-pill ${draft.status}`}>{draft.status}</span>
          <button className="button dark">
            <Save size={16} /> Save article
          </button>
          <button
            className="button danger"
            type="button"
            onClick={() => onDelete(draft)}
          >
            <Trash2 size={15} /> Delete article
          </button>
        </div>
      </div>
      <div className="post-editor-layout">
        <div className="post-editor-main">
          <section className="post-field-card post-story-card">
            <div className="post-section-heading">
              <div>
                <p className="eyebrow">STORY</p>
                <h3>Lead with the decision</h3>
              </div>
              <span className="post-field-note">Required</span>
            </div>
            <AdminField
              label="Title"
              value={draft.title}
              onChange={(value) => set("title", value)}
            />
            <AdminField
              label="Excerpt"
              value={draft.excerpt}
              onChange={(value) => set("excerpt", value)}
              textarea
            />
          </section>
          <section className="post-field-card post-body-card">
            <div className="post-section-heading">
              <div>
                <p className="eyebrow">ARTICLE CONTENT</p>
                <h3>Write and format the article</h3>
              </div>
              <span className="post-field-note">Visual editor</span>
            </div>
            <WysiwygEditor
              value={draft.body}
              onChange={(value) => set("body", value)}
            />
          </section>
        </div>
        <aside className="post-editor-sidebar">
          <section className="post-field-card">
            <div className="post-section-heading compact">
              <div>
                <p className="eyebrow">PUBLISHING</p>
                <h3>Article settings</h3>
              </div>
            </div>
            <label>
              Publication status
              <select
                value={draft.status}
                onChange={(e) => set("status", e.target.value as Post["status"])}
              >
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="published">Published</option>
              </select>
            </label>
            <AdminField
              label="Article URL"
              value={draft.slug}
              onChange={(value) => set("slug", value)}
            />
            <div className="post-url-preview">the1kimkelley.com/blog/{draft.slug || "your-article"}</div>
          </section>
          <section className="post-field-card">
            <div className="post-section-heading compact"><div><p className="eyebrow">HUMAN DIRECTION</p><h3>Contribution context</h3></div></div>
            <AdminField label="Contributor" value={draft.contributor ?? "Kim Kelley"} onChange={(value) => set("contributor", value)} />
            <AdminField label="Topic" value={draft.topic ?? ""} onChange={(value) => set("topic", value)} />
            <AdminField label="Audience" value={draft.audience ?? ""} onChange={(value) => set("audience", value)} textarea />
            <AdminField label="Product / service" value={draft.product ?? ""} onChange={(value) => set("product", value)} />
            <AdminField label="Call to action" value={draft.cta ?? ""} onChange={(value) => set("cta", value)} textarea />
            <AdminField label="Sources (one per line)" value={(draft.sourceNotes ?? []).join("\n")} onChange={(value) => set("sourceNotes", value.split(/\r?\n/).map(item => item.trim()).filter(Boolean))} textarea />
            <AdminField label="Editorial notes" value={draft.editorialNotes ?? ""} onChange={(value) => set("editorialNotes", value)} textarea />
          </section>
          <section className="post-field-card">
            <div className="post-section-heading compact">
              <div>
                <p className="eyebrow">LINKEDIN</p>
                <h3>Publishing status</h3>
              </div>
            </div>
            <p className="helper">
              {draft.linkedinStatus === "published"
                ? "Published automatically with the article, including the article and relevant workshop or service links."
                : draft.linkedinStatus === "failed"
                ? `Publishing needs attention: ${draft.linkedinError || "the LinkedIn connection is not ready."}`
                : "When LinkedIn automation is enabled, publishing this article creates one LinkedIn post after the save."}
            </p>
            {draft.linkedinPostUrl && (
              <a href={draft.linkedinPostUrl} target="_blank" rel="noreferrer" className="post-url-preview">View LinkedIn post</a>
            )}
          </section>
          <section className="post-field-card">
            <div className="post-section-heading compact">
              <div>
                <p className="eyebrow">SEARCH PREVIEW</p>
                <h3>SEO details</h3>
              </div>
            </div>
            <AdminField
              label="SEO title"
              value={draft.seoTitle}
              onChange={(value) => set("seoTitle", value)}
            />
            <AdminField
              label="SEO description"
              value={draft.seoDescription}
              onChange={(value) => set("seoDescription", value)}
              textarea
            />
          </section>
          <div className="post-editor-note">
            <strong>Editorial check</strong>
            <p>AI-created drafts should be reviewed before publishing. Confirm the title, links, and reading flow here first.</p>
          </div>
        </aside>
      </div>
    </form>
  );
}

function AutomationEditor({ state, csrf, refresh, note }: Props) {
  const [settings, setSettings] = useState(state.automation);
  useEffect(() => setSettings(state.automation), [state]);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await api("/api/admin/automation", {
        method: "PUT",
        body: JSON.stringify(settings),
        csrf,
      });
      note("Automation settings saved.");
      await refresh();
    } catch (error) {
      note(
        error instanceof Error
          ? error.message
          : "Could not save automation settings.",
      );
    }
  };
  return (
    <div className="automation-layout">
      <section className="automation-card">
        <div>
          <p className="eyebrow">CONTENT SCHEDULING</p>
          <h2>
            Two decision-useful posts a day - when every connection is ready.
          </h2>
          <p>
            Time zone: Central. The scheduler is idempotent per time slot and
            will not create or post content while its AI or social provider is
            unconfigured.
          </p>
        </div>
        <form onSubmit={save}>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) =>
                setSettings({ ...settings, enabled: e.target.checked })
              }
            />
            <span /> Enable scheduled article publishing
          </label>
          <label>
            Post times (Central)
            <input
              value={settings.times.join(", ")}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  times: e.target.value
                    .split(",")
                    .map((v) => v.trim())
                    .filter(Boolean),
                })
              }
            />
          </label>
          <label>
            Package link path
            <input
              value={settings.packagePath}
              onChange={(e) =>
                setSettings({ ...settings, packagePath: e.target.value })
              }
            />
          </label>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.linkedinEnabled}
              onChange={(e) =>
                setSettings({ ...settings, linkedinEnabled: e.target.checked })
              }
            />
            <span /> Automatically publish a LinkedIn post when an article is published
          </label>
          <button className="button dark">
            <Save size={16} /> Save controls
          </button>
        </form>
      </section>
      <section className="integration-list">
        <Status
          title="OpenAI article engine"
          text={state.integrations.generator.message}
          ready={state.integrations.generator.configured}
        />
        <Status
          title="LinkedIn profile publishing"
          text={state.integrations.linkedin.message}
          ready={state.integrations.linkedin.configured}
        />
        <Status
          title="Google Calendar"
          text={state.integrations.calendar.message}
          ready={state.integrations.calendar.configured}
        />
        <Status
          title="Brevo notifications"
          text={state.integrations.brevo.message}
          ready={state.integrations.brevo.configured}
        />
        <Status
          title="Textbelt form relay"
          text={state.integrations.textbelt.message}
          ready={state.integrations.textbelt.configured}
        />
      </section>
    </div>
  );
}
function Status({
  title,
  text,
  ready,
}: {
  title: string;
  text: string;
  ready: boolean;
}) {
  const needsOAuth =
    !ready &&
    (title === "Google Calendar" || title === "LinkedIn profile publishing");
  return (
    <article className={`status-row ${needsOAuth ? "status-row-oauth" : ""}`}>
      <div className="status-main">
        {ready ? <CheckCircle2 size={19} /> : <CircleAlert size={19} />}
        <div>
          <strong>{title}</strong>
          <p>{text}</p>
        </div>
      </div>
      <div className="status-actions">
        <span className={ready ? "ready" : "pending"}>
          {ready ? "Connected" : "Pending"}
        </span>
        {needsOAuth && (
          <button
            className="oauth-connect-button"
            type="button"
            onClick={() =>
              window.dispatchEvent(new Event("kim-open-oauth-settings"))
            }
          >
            <Link2 size={13} /> Connect OAuth
          </button>
        )}
      </div>
    </article>
  );
}
